// App del camionero — lista de sus viajes/rutas asignados (mobile, nuevo diseño).
// Muestra los viajes (camión/gira) donde choferId == uid. Al tocar uno, entra al
// reparto (CamioneroReparto) para escanear y entregar.
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ThemeProvider, useTheme } from "../../../theme/ThemeProvider";
import { AppHeader, Card, Chip, Button } from "../../../theme/ui";

function fmtFecha(ts) {
  if (!ts?.toDate) return "—";
  return ts.toDate().toLocaleDateString("es-UY", { weekday: "long", day: "numeric", month: "long" });
}

// Chip de "cuándo sale" (hoy / mañana / etc.) — mismo criterio que GestionCamiones.
function cuando(ts) {
  if (!ts?.toDate) return null;
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const f = ts.toDate(); f.setHours(0, 0, 0, 0);
  const d = Math.round((f - hoy) / 86400000);
  if (d === 0) return { label: "Hoy", tone: "danger" };
  if (d === 1) return { label: "Mañana", tone: "warning" };
  if (d < 0) return { label: `Hace ${Math.abs(d)}d`, tone: "neutral" };
  return { label: `En ${d}d`, tone: "accent" };
}

function ViajesInner() {
  const { profile, logout } = useAuth();
  const navigate = useNavigate();
  const uid = profile?.id || profile?.uid || null;
  const t = useTheme();

  const [viajes, setViajes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) { setLoading(false); return; }
    const q = query(collection(db, "camiones"), where("choferId", "==", uid));
    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(v => v.estado === "CERRADO")   // solo viajes cerrados (salieron): listos para repartir
        .sort((a, b) => (a.fechaSalida?.seconds ?? 0) - (b.fechaSalida?.seconds ?? 0));
      setViajes(docs);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [uid]);

  const hora = new Date().getHours();
  const saludo = hora < 12 ? "Buenos días" : hora < 19 ? "Buenas tardes" : "Buenas noches";

  return (
    <div style={{ minHeight: "100vh", background: t.color.bg, color: t.color.text }}>
      <AppHeader
        icon="🚚"
        title="Mis viajes"
        subtitle={`${saludo}, ${profile?.nombre || "Camionero"}`}
        right={<Button size="sm" variant="ghost" onClick={logout}>Salir</Button>}
      />
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "16px", display: "flex", flexDirection: "column", gap: 12 }}>
        {loading ? (
          <p style={{ color: t.color.textMuted, textAlign: "center", padding: "32px 0" }}>Cargando…</p>
        ) : viajes.length === 0 ? (
          <Card style={{ flexDirection: "column", alignItems: "center", textAlign: "center", padding: "32px 20px" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📭</div>
            <div style={{ fontWeight: 700 }}>No tenés viajes asignados</div>
            <div style={{ fontSize: t.font.sm, color: t.color.textMuted, marginTop: 4 }}>
              Cuando el encargado te asigne un viaje o una ruta, aparece acá.
            </div>
          </Card>
        ) : (
          viajes.map(v => {
            const w = cuando(v.fechaSalida);
            const esCamion = v.tipo === "CAMION";
            const enReparto = v.estado === "EN_REPARTO";
            return (
              <Card key={v.id} onClick={() => navigate(`/camionero/viaje/${v.id}`)}>
                <div style={{ fontSize: 30, flexShrink: 0 }}>{esCamion ? "🚛" : "🚚"}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, fontSize: t.font.lg }}>{v.descripcion || (esCamion ? "Camión" : "Gira")}</span>
                    {v.codigo && <Chip tone="neutral">{v.codigo}</Chip>}
                  </div>
                  <div style={{ fontSize: t.font.sm, color: t.color.textMuted, marginTop: 3 }}>
                    {esCamion ? "Camión" : "Gira"} · {fmtFecha(v.fechaSalida)}
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    {w && <Chip tone={w.tone}>{w.label}</Chip>}
                    {enReparto && <Chip tone="accent">En reparto</Chip>}
                  </div>
                </div>
                <span style={{ fontSize: 22, color: t.color.textFaint, flexShrink: 0 }}>›</span>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}

export default function CamioneroViajes() {
  return (
    <ThemeProvider mode="light">
      <ViajesInner />
    </ThemeProvider>
  );
}
